<template>
  <section
    ref="$block"
    :class="{
      'osk-filterable-shelf-block': true,
      'osk-filterable-shelf-block_withTags': withFilterOptions,
    }"
  >
    <div class="osk-wrapper">
      <h2
        v-if="data.title"
        class="osk-filterable-shelf-block__title"
      >
        {{ data.title }}
      </h2>

      <div
        v-if="options.length > 1"
        :class="{
          'osk-filterable-shelf-block__list': true,
          'osk-filterable-shelf-block__list_before': isBefore,
          'osk-filterable-shelf-block__list_after': isAfter,
        }"
      >
        <div
          ref="$slider"
          class="osk-filterable-shelf-block__hotFilters"
        >
          <UikitButton
            v-for="(item, index) in options"
            :key="index"
            :class="{
              'osk-filterable-shelf-block__hotFilter': true,
              'osk-filterable-shelf-block__hotFilter_active': index === activeIndex,
            }"
            light-outline
            size="s"
            :shadow-loading="getProductsLoading && index === activeIndex"
            @click="handleFilterClick(item, index)"
          >
            {{ item.title }}
          </UikitButton>
        </div>
      </div>

      <UikitSwiper
        ref="$swiper"
        navigation
        :breakpoints="sliderBreakpoints"
        :css-mode="!isDesktop"
        :arrow-back="!isMounted || !isDesktop"
        off
        class="osk-filterable-shelf-block__swiper"
        @slide-change="handleSlideChange"
      >
        <UikitSwiperSlide
          v-for="(product, index) in products"
          :key="index"
        >
          <UikitProductCard
            :index="index"
            :product="product"
            is-seller-hidden
            hide-size
            :centered="!isMounted || !isDesktop"
            use-swiper-analytics
            :analytics="analytics"
            @product-click="handleProductClick"
          />
        </UikitSwiperSlide>
      </UikitSwiper>

      <div
        v-if="link"
        class="osk-filterable-shelf-block__footer"
      >
        <UikitButton
          class="osk-filterable-shelf-block__button"
          outline
          :size="!isMounted || isDesktop ? 's' : 'm'"
          @click="goToNextPage"
        >
          Смотреть все товары
        </UikitButton>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { Home } from '~/restAPI/Home';
import { useApi } from '~/restAPI';
import {
  PrimaryContentDTO, PrimaryPageProductDTO, ShelfWithFiltersDTO,
  ShelfWithFiltersOptionDTO,
} from '~/restAPI/data-contracts';
import { AnalyticsEvents } from '~/types/analytics';

const sliderBreakpoints = {
  0: {
    slidesPerView: 1,
    slidesPerGroup: 1,
    spaceBetween: 16,
    navigation: { enabled: true },
  },
  500: {
    slidesPerView: 2,
    slidesPerGroup: 2,
    spaceBetween: 0,
    navigation: { enabled: false },
  },
  580: {
    slidesPerView: 2.5,
    slidesPerGroup: 2,
    spaceBetween: 0,
    navigation: { enabled: false },
  },
  700: {
    slidesPerView: 3,
    slidesPerGroup: 3,
    spaceBetween: 0,
    navigation: { enabled: false },
  },
  1024: {
    slidesPerView: 4,
    slidesPerGroup: 4,
    spaceBetween: 24,
    navigation: { enabled: true },
  },
};

const props = defineProps<{
  data: PrimaryContentDTO
}>();

const $block = ref<ComponentPublicInstance>();
const $swiper = ref();
const $slider = ref();

const { isMouseMove, isBefore, isAfter } = useOverflowScrollWrapper($slider);
const { createApiInstance } = useApi();
const { isDesktop, isMounted } = useUiBreakpoints();
const { $addEvent } = useNuxtApp();

const homeApi = createApiInstance(Home);

const activeIndex = ref(0);

const content = computed(() => props.data.content as ShelfWithFiltersDTO);
const withFilterOptions = computed(() => content.value.withFilterOptions);
const presetFilter = computed(() => content.value.presetFilter);
const options = computed(() => content.value.options ?? []);
const activeFilter = computed(() => options.value[activeIndex.value].filter);

const products = ref<PrimaryPageProductDTO[]>(getDefaultProducts());

const link = computed(() => genSPALink(props.data.additionalData?.jumpLink));

const {
  pending: getProductsLoading,
  execute: getProducts,
} = await useAsyncData(() => homeApi.postApiV2HomeFilterableContentItems({
  baseCategory: presetFilter.value?.baseCategory,
  presets: presetFilter.value?.presets,
  filters: activeFilter.value,
}).then((res) => {
  const { data } = res.data;

  if (!data) return;

  products.value = data.map((item) => item.content as PrimaryPageProductDTO);
}), { immediate: false });

getProductsLoading.value = false;

const analytics = computed(() => ({
  chapter: props.data.contentType,
  chapter_index: props.data.chapterIndex,
  segment_id: props.data.segmentId,
  id_chapter: props.data.id,
}));
const allIds = computed(() => products.value.map((i) => i.id!));

const { event, clickEvent } = useSwiperAnalytics(
  $swiper,
  $block,
  allIds,
  analytics.value,
  sliderBreakpoints,
);

function handleProductClick({ productId, index }: { productId?: number, index?: number }) {
  clickEvent({
    id_item: String(productId),
    index,
    item: 'product',
  });
}

function handleSlideChange() {
  event();
}

function getDefaultProducts() {
  const result = options.value[activeIndex.value].contents ?? [];

  return result.map((item) => item.content as PrimaryPageProductDTO);
}

function goToNextPage() {
  if (!link.value) return;

  navigateTo(link.value);

  $addEvent(AnalyticsEvents.ShowAllClick, analytics.value);
}

function handleFilterClick(item: ShelfWithFiltersOptionDTO, index: number) {
  if (isMouseMove.value) return;

  activeIndex.value = index;

  getProducts();

  const brandId = item.filter?.brand && item.filter?.brand[0];

  if (!brandId) return;

  $addEvent(AnalyticsEvents.ClickEvent, {
    ...analytics.value,
    item: 'brand',
    id_item: String(brandId),
    action: 'filter',
  });
}
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

.osk-filterable-shelf-block {
  padding-bottom: 42px;

  @include media-query(lg-and-up) {
    padding-bottom: 82px;
  }

  &__title {
    @include overflow-ellipsis;
    @include font-heading;
    padding-bottom: 22px;

    @include media-query(lg-and-up) {
      text-transform: uppercase;
      text-align: center;
      padding-bottom: 34px;
    }
  }

  @media screen and (max-width: 1023px) {
    .swiper-slide {
      display: flex;
    }

    .osk-product-card {
      margin: auto;

      .osk-product-card__card-img {
        margin-bottom: 20px;
        height: auto;
        width: 193px;
        height: 182px;

        &:before {
          display: none;
        }
      }

      .osk-product-card__card-img-pic {
        margin-top: 0;
        object-fit: cover;
        height: 100%;
      }

      .osk-likes-count__icon {
        width: 24px;
        height: 24px;
      }

      .osk-product-card__card-icon {
        top: 3px;
        left: -15px;
      }

      .osk-product-card__card-top {
        top: 0;
        right: -20px;
      }

      .osk-product-card__card-info {
        padding-top: 0;
        margin-bottom: 15px;
      }

      .osk-product-card__card-info-brand {
        margin-bottom: 4px;
      }

      .osk-product-card__card-info-price-value {
        margin-right: 0;
      }

      .osk-product-card__content {
        min-width: 100%;
      }
    }

    .osk-swiper {
      .osk-swiper-button-prev {
        top: 100px;
        left: 0;
        width: 24px;
        height: 24px;
      }

      .osk-swiper-button-next {
        top: 100px;
        right: 0;
      }
    }
  }

  &_withTags {
    .osk-swiper {
      @include media-query(lg-and-up) {
        .osk-swiper-button-prev {
          top: -127px;
        }

        .osk-swiper-button-next {
          top: -127px;
        }
      }
    }
  }

  &__footer {
    padding-top: 24px;

    @include media-query(lg-and-up) {
      width: 100%;
      display: flex;
      justify-content: center;
      padding-top: 42px;
    }
  }

  &__button {
    width: 100%;

    @include media-query(lg-and-up) {
      width: auto;
    }
  }

  &__hotFilters {
    display: flex;
    width: 100%;
    overflow-y: auto;
    scrollbar-width: none;
    margin-bottom: 24px;

    &::-webkit-scrollbar {
      display: none;
    }

    @include media-query(lg-and-up) {
      margin-bottom: 32px;
    }
  }

  &__hotFilter {
    flex-shrink: 0;
    height: 36px;
    border-radius: 100px;

    &:hover {
      opacity: 1;
      border-color: $grey-3;
    }

    &:active {
      opacity: 0.6;
      border-color: $grey-3;
    }

    &_active {
      background: $grey-4;
      border-color: $grey-4 !important;
      pointer-events: none;
    }

    & + & {
      margin-left: $indent-compact;
    }

    @include media-query(lg-and-up) {
      height: 38px;
      border-radius: 2px;
    }
  }

  .osk-swiper_off {
    .swiper-wrapper {
      overflow: visible;
      justify-content: space-between;
    }

    .swiper-slide {
      max-width: 100%;
      flex-shrink: 0;

      @include media-query(lg-and-up) {
        max-width: 23%;
        margin-right: 24px;
      }
    }
  }

  &__list {
    position: relative;

    &:before {
      content: '';
      width: 30px;
      height: 38px;
      background: linear-gradient(90deg, #fff, rgba(255, 255, 255, 0));
      position: absolute;
      left: 0;
      top: 0;
      opacity: 0;
      visibility: hidden;
      transition: all linear .1s;
    }

    &:after {
      content: '';
      width: 30px;
      height: 38px;
      background: linear-gradient(270deg, #fff, rgba(255, 255, 255, 0));
      position: absolute;
      right: 0;
      top: 0;
      opacity: 0;
      visibility: hidden;
      transition: all linear .1s;
    }

    &_before {
      &:before {
        opacity: 1;
        visibility: visible;
        z-index: 1;
      }
    }

    &_after {
      &:after {
        opacity: 1;
        visibility: visible;
      }
    }
  }

  .osk-swiper-button-prev,
  .osk-swiper-button-next {
    .osk-icon {
      @include media-query(lg-and-down) {
        width: 20px;
        height: 20px;
      }
    }
  }
}

.osk-home-gender-page_isJournalMode {
  .osk-blocks-text-controls_no-image + .osk-filterable-shelf-block {
    margin-top: -18px;

    @include media-query(lg-and-up) {
      margin-top: 0;
    }
  }

  .osk-filterable-shelf-block {
    .osk-product-card__card-icon,
    .osk-product-card__card-top {
      display: none;
    }

    @media screen and (max-width: 1024px) {
      .osk-swiper {
        .osk-swiper-button-prev {
          top: 166px;
        }

        .osk-swiper-button-next {
          top: 166px;
        }
      }

      .osk-product-card__card-info-name {
        margin-bottom: 16px;
      }

      .osk-product-card__card-img {
        @include image-bg-grey;
        width: 264px;
        height: 352px;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: flex-end;
        margin-bottom: 24px;

        &:before {
          display: block;
        }
      }

      .osk-product-card__card-img-pic {
        display: block;
        width: 100%;
        object-fit: contain;
        object-position: center;
        height: 270px;
        margin-bottom: 24px;
      }
    }
  }
}
</style>
